// Code-aware error copy for the conversation-create path (the compose modal
// and the shared "DM this member" affordance). Those mutations funnel non-2xx
// through `throwApiError`, which re-throws a `DomainError` carrying the
// server's `{ code, message?, details? }` envelope.
//
// The create-conversation endpoint's most user-relevant failure is the
// cold-outreach throttle, which rejects with a code-only `RATE_LIMITED`
// (no server `message`). Both call sites previously discarded the code and
// showed a flat fallback line — so a throttled member got no hint a limit was
// hit (bug-bash 7/2, Section 1). This maps the codes the endpoint actually
// emits to translated copy under `Messages.errors.codes.*`; everything else
// resolves through the localized `Errors` catalog, then a caller-supplied key.
//
// Local to the messaging feature on purpose — `lib/api-error.ts` is the shared
// envelope-narrowing surface and stays code-agnostic. Friendly copy is a
// product concern that belongs next to the screens that show it (mirrors
// feed/feed-error-copy.ts).

import { type useTranslations } from '@/i18n/translations';

import { parseApiErrorCode } from '../../lib/api-error';
import type { DescribeError } from '../../lib/error-message';

type MessagesTranslate = ReturnType<typeof useTranslations<'Messages'>>;

// Maps the conversation-create codes the server emits to their catalog key
// under `errors.codes.*`. Codes absent here fall through to the `Errors`
// catalog, then the caller's fallback. RATE_LIMITED covers both the per-request gate and the
// cold-outreach throttle (untrusted account, no shared community, cap on new
// conversations) — "new conversations" covers both on this endpoint.
const MESSAGE_CODE_KEY = {
  RATE_LIMITED: 'errors.codes.RATE_LIMITED',
  'messaging/blocked': 'errors.codes.messaging/blocked',
  'messaging/recipient_not_reachable':
    'errors.codes.messaging/recipient_not_reachable',
  NOT_FOUND: 'errors.codes.NOT_FOUND',
} as const;

// Maps a thrown conversation-create error to translated user-facing copy.
// Never returns a raw machine code: known codes get curated copy, other
// cataloged codes get the shared `Errors` copy, everything else gets
// `fallbackKey`.
export function messageErrorMessage(
  err: unknown,
  describe: DescribeError,
  t: MessagesTranslate,
  fallbackKey: Parameters<MessagesTranslate>[0],
): string {
  const code = parseApiErrorCode(err);
  if (code !== null) {
    const key = MESSAGE_CODE_KEY[code as keyof typeof MESSAGE_CODE_KEY];
    if (key !== undefined) return t(key);
  }
  return describe(err, t(fallbackKey));
}
